import React from 'react';
import { SortableContainer } from 'react-sortable-hoc';
import {
  TableContainer,
  Grid,
  Table,
  TableCell,
  Typography,
  TableRow,
  TableHead,
  Checkbox,
  TableBody,
  TableSortLabel,
} from '@material-ui/core';
import { SearchBar } from './SearchBar';
import { SortableItem } from './SortableItem';
import { RefreshList } from './RefreshList';
import { Toolbar } from '../../../pages/titlesCM/titlesListCM/components/Toolbar';
import { DragHandle } from './DragHandle';
import LoadingItem from './LoadingItem';
import { ISortColumnData } from '../TableDragDrop';

interface ISortableListProps {
  classes: { [key: string]: string };
  items: ITitle[];
  sortColumnData: ISortColumnData;
  sortTitlesByColumn: (field: string) => void;
  writers: IUser[];
  searchText: string;
  handleSearchText: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  clearSearchText: () => void;
  getWriterName: (writerId: string, writers: IUser[]) => string;
  refreshTitles: (showBackdrop?: boolean) => Promise<void>;
  handleCheckbox: (checked: boolean, title: ITitle) => void;
  clearSelectedCheckboxes: () => void;
  displayToolbar: boolean;
  selectedItems: Map<string, ITitle>;
  isAllCheckboxesSelected: boolean;
  handleSelectAllCheckbox: (isChecked: boolean) => void;
  selectedAllItems: boolean;
  countItemsSelected: number;
  refetchTitles: (showBackdrop?: boolean) => void;
  filterStatus: number | null;
  permissions: IPermission;
  sitesList: string[];
  loading?: boolean;
  onOpenUpdateDrawer?: (id: string) => void;
}

export const SortableList = SortableContainer(
  ({
    classes,
    items,
    sortColumnData,
    sortTitlesByColumn,
    writers,
    searchText,
    handleSearchText,
    clearSearchText,
    getWriterName,
    refreshTitles,
    handleCheckbox,
    clearSelectedCheckboxes,
    displayToolbar,
    selectedItems,
    isAllCheckboxesSelected,
    handleSelectAllCheckbox,
    selectedAllItems,
    countItemsSelected,
    refetchTitles,
    filterStatus,
    permissions,
    sitesList,
    loading,
    onOpenUpdateDrawer,
  }: ISortableListProps) => {
    return (
      <TableContainer className={classes.tableContainer}>
        <Grid container>
          <Grid item>
            <RefreshList refreshTitles={refreshTitles} />
          </Grid>
          <Grid item xs={5}>
            <SearchBar
              classes={classes}
              searchText={searchText}
              handleSearchText={handleSearchText}
              clearSearchText={clearSearchText}
            />
          </Grid>
          {displayToolbar && (
            <Grid item xs={6}>
              <Toolbar
                selectedRows={selectedItems}
                refetchTitles={refetchTitles}
                filterStatus={filterStatus}
                permissions={permissions}
                sitesList={sitesList}
                clearSelectedCheckboxes={clearSelectedCheckboxes}
              />
            </Grid>
          )}
        </Grid>

        <Table className={classes.table} aria-label="titles table">
          <TableHead>
            <TableRow>
              <TableCell>
                <DragHandle />
              </TableCell>
              <TableCell component="th" scope="row">
                <Checkbox
                  checked={isAllCheckboxesSelected}
                  color="primary"
                  value={selectedAllItems}
                  onChange={(e) => handleSelectAllCheckbox(e.target.checked)}
                />
              </TableCell>
              <TableCell>&nbsp;</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Language</TableCell>
              <TableCell>Site</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Niche</TableCell>
              <TableCell>Tag</TableCell>
              <TableCell>
                <TableSortLabel
                  direction={sortColumnData.sortDirection}
                  onClick={() => sortTitlesByColumn('createdAt')}
                  className={classes.sortIcon}
                  hideSortIcon={!sortColumnData.isActive}
                >
                  Created at
                </TableSortLabel>
              </TableCell>
              <TableCell>Writer</TableCell>
              <TableCell>Account Manager</TableCell>
              <TableCell>Post URL</TableCell>
              <TableCell>
                <TableSortLabel
                  direction={sortColumnData.sortDirection}
                  onClick={() => sortTitlesByColumn('publishPost')}
                  className={classes.sortIcon}
                  hideSortIcon={!sortColumnData.isActive}
                >
                  Publish Post
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  direction={sortColumnData.sortDirection}
                  onClick={() => sortTitlesByColumn('deadline')}
                  className={classes.sortIcon}
                  hideSortIcon={!sortColumnData.isActive}
                >
                  Deadline
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <>
                <LoadingItem />
                <LoadingItem />
                <LoadingItem />
                <LoadingItem />
                <LoadingItem />
              </>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={16} component="td">
                  <Typography align="center">
                    Sorry, no matching records found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((title, index) => (
                <SortableItem
                  key={`title-${title.title}-${title.id}-${countItemsSelected}`}
                  index={index}
                  checked={!!selectedItems.get(title.id)}
                  title={title}
                  writers={writers}
                  getWriterName={getWriterName}
                  handleCheckbox={handleCheckbox}
                  onOpenUpdateDrawer={onOpenUpdateDrawer}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  },
);
